import { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { App } from "./App";
import { DEFAULT_ROUTES } from "./constants/routes";

import { ErrorBoundary } from "./components/error-boundary";
import { AnalyticsProvider } from "./features/analytics";
import { AuthProvider } from "./features/authentication";
import { ListProvider as LicenseFeeListProvider } from "./features/license-fee/list/context/use-list";
import { ListProvider as PrivateRoomContractsListProvider } from "./features/private-room/contracts/list/context/use-list";
import { useUserAuth } from "./hooks/use-user-auth";
import { BookingCreate } from "./pages/private-room/booking-create";
import BokaoList from "./pages/private-room/bookings";
import { PrivateRoomContractsList } from "./pages/private-room/contracts-create/list/private-room-contracts-list";
import { LicenseFeeList } from "./pages/license-fee/list/license-fee-list";
import ModalProposalPrivateRoom, {
  action as proposalAction,
  loader as proposalLoader,
} from "./pages/private-room/modal-proposal-private-room";
import ModalProposalLinks, {
  loader as proposalLinksLoader,
} from "./pages/private-room/modal-proposal-links";
import {
  PhotosModal,
  loader as photosLoader,
} from "./pages/private-room/photos-modal";
import { PageErrorBoundary } from "./components/page-error-boundary";
import { BookingDownsell } from "./pages/private-room/booking-downsell";
import {
  LicenseFeeCreate,
  action as createLicenseFeeAction,
  loader as createLicenseFeeLoader,
} from "./pages/license-fee/create";
import { MatchMaking, loader as matchMakingLoader } from "./pages/match-making";
import {
  LicenseFeeEdit,
  loader as updateLicenseFeeLoader,
  action as updateLicenseFeeAction,
} from "./pages/license-fee/edit";
import {
  CancelModal,
  action as cancelModalAction,
} from "./pages/license-fee/modal-cancel";
import {
  ModalCancelResult,
  loader as cancelResultLoader,
} from "./pages/license-fee/modal-cancel-result";
import {
  PrivateOfficeContractsCreate,
  loader as privateOfficeContractsCreateLoader,
} from "./pages/private-room/contracts-create";
import Vision360 from "./pages/vision-360";
import AtlasPage from "./pages/atlas/atlas-page";
import Proposals from "./pages/vision-360/proposals";
import AIModeWizard from "./features/vision-360/ai-mode/AIModeWizard";
import PartnerDemoWizard from "./features/vision-360/partner-demo/PartnerDemoWizard";
import ProposalRoutePage from "./features/vision-360/proposal-route/ProposalRoutePage";
import { PoContractsCreateFlow } from "./features/private-room/bookings/layouts/po-contracts-create-flow";
import { BookingFlowProvider } from "./features/private-room/bookings/context/use-booking-flow";
import { loader as privateOfficeContractsUpdateLoader } from "./pages/private-room/contracts-update";
import { updateHistoryLoader } from "./pages/private-room/admin/components/update-history-loader";
import { UpdateHistoryRoute } from "./pages/private-room/admin/components/update-history-route";
import { PrivateRoomAdminDetailsRoute } from "./pages/private-room/admin/components/private-room-admin-details-route";
import { privateRoomAdminDetailsLoader } from "./pages/private-room/admin/components/private-room-admin-details-loader";
import { PoContractsUpdateFlowProvider } from "./features/private-room/bookings/context/use-po-contracts-update-flow";
import { PoContractsUpdateFlow } from "./features/private-room/bookings/layouts/po-contracts-update-flow";
import {
  PrivateOfficeContractsCancel,
  loader as privateOfficeContractsCancelLoader,
  action as privateOfficeContractsCancelAction,
} from "./pages/private-room/contracts-cancel";
import { ScoreDetailsModal } from "./pages/private-room/score-details-modal";
import { PrivateOfficeUpdateRequests } from "./pages/private-room/update-requests";
import { RejectModal } from "./pages/private-room/update-requests/components";
import { Unauthorized } from "./pages/private-office/external";
import {
  PrivateOfficeNewCreate,
  newCreatePrivateOfficeLoader,
} from "./pages/private-office/new-create";
import { PrivateOfficeEdit } from "./pages/private-office/edit";
import { editPrivateOfficeLoader } from "./pages/private-office/edit/loader";
import PrivateRoomAdmin from "./pages/private-room/admin";
import ProposalDetail from "./pages/vision-360/proposal-detail";
import { RouteFallback } from "./components/route-fallback";

const Signin = lazy(() => import("@/pages/signin"));

const AuthRoute = ({ children }: any) => {
  const userAuth = useUserAuth();
  return userAuth.isLoggedIn() ? (
    <AuthProvider>
      <AnalyticsProvider>{children}</AnalyticsProvider>
    </AuthProvider>
  ) : (
    <Navigate to={DEFAULT_ROUTES.SIGNIN} replace />
  );
};

const AuthLogin = ({ children }: any) => {
  const userAuth = useUserAuth();
  return userAuth.isLoggedIn() ? (
    <Navigate to={DEFAULT_ROUTES.PRIVATE_OFFICE_BOOKINGS} replace />
  ) : (
    children
  );
};

const IndexRedirect = () => {
  const userAuth = useUserAuth();
  return (
    <Navigate
      to={
        userAuth.isLoggedIn()
          ? DEFAULT_ROUTES.PRIVATE_OFFICE_BOOKINGS
          : DEFAULT_ROUTES.SIGNIN
      }
    />
  );
};

// Define the routes with the loader
const router = createBrowserRouter([
  {
    path: DEFAULT_ROUTES.INDEX,
    element: <App />,
    children: [
      {
        index: true,
        element: <IndexRedirect />,
      },
      {
        path: DEFAULT_ROUTES.PO_AGENT,
        element: (
          <AuthRoute>
            <MatchMaking />
          </AuthRoute>
        ),
        loader: matchMakingLoader,
      },
      {
        path: DEFAULT_ROUTES.SIGNIN,
        element: (
          <AuthLogin>
            <Signin />
          </AuthLogin>
        ),
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_BOOKINGS,
        element: (
          <AuthRoute>
            <ErrorBoundary fallback={<div>error</div>}>
              <BokaoList />
            </ErrorBoundary>
          </AuthRoute>
        ),
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_BOOKING_CREATE,
        element: (
          <AuthRoute>
            <BookingCreate />
          </AuthRoute>
        ),
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_CREATE,
        element: (
          <AuthRoute>
            <BookingFlowProvider>
              <PoContractsCreateFlow />
            </BookingFlowProvider>
          </AuthRoute>
        ),
        errorElement: <PageErrorBoundary />,
        loader: privateOfficeContractsCreateLoader,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_UPDATE,
        element: (
          <AuthRoute>
            <PoContractsUpdateFlowProvider>
              <PoContractsUpdateFlow />
            </PoContractsUpdateFlowProvider>
          </AuthRoute>
        ),
        loader: privateOfficeContractsUpdateLoader,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_CANCEL,
        element: (
          <AuthRoute>
            <PrivateOfficeContractsCancel />
          </AuthRoute>
        ),
        loader: privateOfficeContractsCancelLoader,
        action: privateOfficeContractsCancelAction,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_BOOKING_DOWNSELL,
        element: (
          <AuthRoute>
            <BookingDownsell />
          </AuthRoute>
        ),
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_LIST,
        element: (
          <AuthRoute>
            <PrivateRoomContractsListProvider>
              <Suspense fallback={<RouteFallback />}>
                <PrivateRoomContractsList />
              </Suspense>
            </PrivateRoomContractsListProvider>
          </AuthRoute>
        ),
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_CONTRACTS_CREATE,
        element: (
          <AuthRoute>
            <PrivateOfficeContractsCreate />
          </AuthRoute>
        ),
        errorElement: <PageErrorBoundary />,
        loader: privateOfficeContractsCreateLoader,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_UNAUTHORIZED,
        element: <Unauthorized />,
      },
      // Both viewer paths resolve through the same page — the template (new
      // standard vs legacy) is picked by the fetched proposal's own type, not
      // by the URL. /proposta-smb survives only as an alias for already-sent
      // SMB links (PED-12653).
      {
        path: DEFAULT_ROUTES.VISION_360_PROPOSAL,
        element: <ProposalRoutePage />,
      },
      {
        path: DEFAULT_ROUTES.VISION_360_PROPOSAL_SMB,
        element: <ProposalRoutePage />,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_CREATE_EXTERNAL,
        element: <PrivateOfficeNewCreate />,
        loader: newCreatePrivateOfficeLoader,
        errorElement: <PageErrorBoundary />,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_EDIT_EXTERNAL,
        element: <PrivateOfficeEdit />,
        loader: editPrivateOfficeLoader,
        errorElement: <PageErrorBoundary />,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_CREATE,
        element: (
          <AuthRoute>
            <PrivateOfficeNewCreate />
          </AuthRoute>
        ),
        loader: newCreatePrivateOfficeLoader,
        errorElement: <PageErrorBoundary />,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_EDIT,
        element: (
          <AuthRoute>
            <PrivateOfficeEdit />
          </AuthRoute>
        ),
        loader: editPrivateOfficeLoader,
        errorElement: <PageErrorBoundary />,
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_ADMIN,
        element: (
          <AuthRoute>
            <ErrorBoundary fallback={<div>error</div>}>
              <Suspense fallback={<RouteFallback />}>
                <PrivateRoomAdmin />
              </Suspense>
            </ErrorBoundary>
          </AuthRoute>
        ),
        children: [
          {
            path: ":roomId/photos",
            element: (
              <AuthRoute>
                <PhotosModal />
              </AuthRoute>
            ),
            loader: photosLoader,
          },
          {
            path: "proposal",
            element: (
              <AuthRoute>
                <ModalProposalPrivateRoom />
              </AuthRoute>
            ),
            loader: proposalLoader,
            action: proposalAction,
            children: [
              {
                path: "links",
                element: <ModalProposalLinks />,
                loader: proposalLinksLoader,
              },
            ],
          },
          {
            path: ":roomId/score-details",
            element: (
              <AuthRoute>
                <ScoreDetailsModal />
              </AuthRoute>
            ),
          },
          {
            path: ":roomId/update-history",
            element: <UpdateHistoryRoute />,
            loader: updateHistoryLoader,
          },
          {
            path: ":roomId/details",
            element: (
              <AuthRoute>
                <PrivateRoomAdminDetailsRoute />
              </AuthRoute>
            ),
            loader: privateRoomAdminDetailsLoader,
          },
        ],
      },
      {
        path: DEFAULT_ROUTES.PRIVATE_OFFICE_UPDATE_REQUESTS,
        element: (
          <AuthRoute>
            <ErrorBoundary fallback={<div>error</div>}>
              <PrivateOfficeUpdateRequests />
            </ErrorBoundary>
          </AuthRoute>
        ),
        children: [
          {
            path: DEFAULT_ROUTES.PRIVATE_OFFICE_UPDATE_REQUESTS_REJECT,
            element: (
              <AuthRoute>
                <RejectModal />
              </AuthRoute>
            ),
          },
        ],
      },
      {
        path: DEFAULT_ROUTES.VISION_360_PROPOSALS,
        element: (
          <AuthRoute>
            <ErrorBoundary fallback={<PageErrorBoundary />}>
              <Proposals />
            </ErrorBoundary>
          </AuthRoute>
        ),
      },
      {
        path: DEFAULT_ROUTES.VISION_PROPOSAL_DETAIL,
        element: (
          <AuthRoute>
            <ErrorBoundary fallback={<PageErrorBoundary />}>
              <ProposalDetail />
            </ErrorBoundary>
          </AuthRoute>
        ),
      },
      {
        path: `${DEFAULT_ROUTES.VISION_360}/*`,
        element: (
          <AuthRoute>
            <ErrorBoundary fallback={<div>error</div>}>
              <Vision360 />
            </ErrorBoundary>
          </AuthRoute>
        ),
        children: [
          {
            path: "setup",
            element: <Vision360 />,
          },
          {
            path: "curation",
            element: <Vision360 />,
          },
          {
            path: "long-list",
            element: <Vision360 />,
          },
          {
            path: "short-list",
            element: <Vision360 />,
          },
          {
            path: "ai",
            element: <AIModeWizard />,
          },
          {
            path: "partner-demo",
            element: <PartnerDemoWizard />,
          },
        ],
      },
      {
        path: DEFAULT_ROUTES.LICENSE_FEE_LIST,
        element: (
          <AuthRoute>
            <LicenseFeeListProvider>
              <Suspense fallback={<RouteFallback />}>
                <LicenseFeeList />
              </Suspense>
            </LicenseFeeListProvider>
          </AuthRoute>
        ),
        children: [
          {
            path: ":licenseFeeId/cancel",
            element: (
              <AuthRoute>
                <CancelModal />
              </AuthRoute>
            ),
            action: cancelModalAction,
            children: [
              {
                path: "result",
                element: <ModalCancelResult />,
                loader: cancelResultLoader,
              },
            ],
          },
        ],
      },
      {
        path: DEFAULT_ROUTES.LICENSE_FEE_CREATE,
        loader: createLicenseFeeLoader,
        action: createLicenseFeeAction,
        errorElement: <PageErrorBoundary />,
        element: (
          <AuthRoute>
            <LicenseFeeCreate />
          </AuthRoute>
        ),
      },
      {
        path: DEFAULT_ROUTES.LICENSE_FEE_UPDATE,
        loader: updateLicenseFeeLoader,
        action: updateLicenseFeeAction,
        errorElement: <PageErrorBoundary />,
        element: (
          <AuthRoute>
            <LicenseFeeEdit />
          </AuthRoute>
        ),
      },
      {
        path: DEFAULT_ROUTES.ATLAS,
        element: (
          <AuthRoute>
            <AtlasPage />
          </AuthRoute>
        ),
      },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
